import { Box, Flex, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { Handle, Position } from "@xyflow/react";
import { MdClose, MdHelpOutline } from "react-icons/md";
import { NODE_TYPES } from "./nodeTypes";

function ColorIcon({ color, Icon }) {
  return (
    <Flex
      w="28px"
      h="28px"
      borderRadius="md"
      bg={color}
      align="center"
      justify="center"
      color="white"
      flexShrink={0}
    >
      <Icon size={16} />
    </Flex>
  );
}

export default function WorkflowNode({ id, data, selected }) {
  const meta = NODE_TYPES[data?.type] || NODE_TYPES.start;
  const Icon = meta?.icon || MdHelpOutline;
  const outputs = meta?.outputs || ["default"];
  const onDelete = data?._onDelete;
  const onSelect = data?._onSelect;

  return (
    <Box
      bg="white"
      borderWidth="2px"
      borderColor={selected ? "blue.500" : "gray.200"}
      borderRadius="md"
      boxShadow="md"
      minW="220px"
      maxW="260px"
      onClick={(e) => { e.stopPropagation(); onSelect && onSelect(id); }}
    >
      {/* No incoming handle on start nodes */}
      {data?.type !== "start" && (
        <Handle type="target" position={Position.Top} style={{ background: "#94a3b8" }} />
      )}

      <Flex align="center" gap={2} p={2}>
        <ColorIcon color={meta?.color || "gray.500"} Icon={Icon} />
        <Box flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
            {meta?.label || data?.type}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {data?.subtitle || _describe(data?.type, data) || meta?.category}
          </Text>
        </Box>
        {onDelete && data?.type !== "start" && (
          <Tooltip label="Delete node">
            <IconButton
              size="xs"
              variant="ghost"
              icon={<MdClose />}
              aria-label="Delete"
              onClick={(e) => { e.stopPropagation(); onDelete(id); }}
            />
          </Tooltip>
        )}
      </Flex>

      {/* Output handles. For conditional nodes label each one. */}
      {outputs.length === 0 ? null : outputs.length === 1 ? (
        <Handle type="source" position={Position.Bottom} style={{ background: "#94a3b8" }} />
      ) : (
        <Flex justify="space-around" pb={1}>
          {outputs.map((handle) => (
            <Box key={handle} position="relative" px={2} py={1}>
              <Text fontSize="10px" color="gray.500" textAlign="center">
                {handle}
              </Text>
              <Handle
                id={handle}
                type="source"
                position={Position.Bottom}
                style={{
                  background: handle === "yes" ? "#16a34a" : handle === "no" ? "#dc2626" : "#94a3b8",
                  position: "absolute",
                  bottom: "-6px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              />
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
}

function _describe(type, data) {
  if (!type || !data) return null;
  if (type === "if_time")
    return `${data.operator || "between"} ${data.from || "?"}-${data.to || "?"}`;
  if (type === "forward_call") return data.number || "configure number";
  if (type === "wait_seconds") return data.seconds ? `${data.seconds}s` : "configure";
  if (type === "wait_until_event_offset")
    return `${data.event_field || "event_time"} + ${data.offset_seconds ?? 0}s`;
  if (type === "send_sms") return data.to || "configure";
  if (type === "place_call") return data.to || "configure";
  if (type === "book_pool_slot") return data.pool_id ? "pool: …" : "configure pool";
  if (type === "call_webhook") return data.url || "configure URL";
  if (type === "set_attribute") return data.key ? `${data.key} = …` : "configure";
  return null;
}
